import {createSelector} from 'reselect';
import {find as _find, forEach as _forEach, isEmpty as _isEmpty} from 'lodash';
import {Select as CommonSelect} from '@gisatcz/ptr-state';
import configuration from '../data/configuration';
import attributes from '../data/attributes';
import interferometryData from './InterferometryData/actions';
import interferometryDataSelectors from './InterferometryData/selectors';
import styles from '../data/styles';
import enumerations from '../data/enumerations';

const getActiveVisualizationAttributes = createSelector(
	[
		state =>
			CommonSelect.components.get(state, 'visualizations', 'activeSection'),
		state =>
			CommonSelect.components.get(
				state,
				'visualizations',
				'riskAssessmentActiveSection'
			),
	],
	(activeSection, activeRiskSection) => {
		if (activeSection === 'interferometry') {
			return attributes[activeSection];
		} else {
			return attributes[activeSection][activeRiskSection];
		}
	}
);

const getMapPopupAttributesOrder = createSelector(
	[
		state =>
			CommonSelect.components.get(state, 'visualizations', 'activeSection'),
	],
	activeSection => {
		return configuration[activeSection].popupAttributeKeys;
	}
);

const getLayersStyleConfig = createSelector(
	[state => CommonSelect.app.getLocalConfiguration(state, 'data')],
	configuration => {
		return configuration.layerStyles;
	}
);

const getPairConfig = createSelector(
	[state => CommonSelect.app.getLocalConfiguration(state, 'data')],
	configuration => {
		return configuration.bigSmallMapPairs;
	}
);

const getListAttributesOrder = createSelector(
	[
		state =>
			CommonSelect.components.get(state, 'visualizations', 'activeSection'),
		state =>
			CommonSelect.components.get(
				state,
				'visualizations',
				'riskAssessmentActiveSection'
			),
	],
	(activeSection, activeRiskSection) => {
		if (activeSection === 'interferometry') {
			return configuration[activeSection].listAttributeKeys;
		} else {
			if (activeRiskSection === 'zones') {
				return configuration[activeSection].zonesListAttributeKeys;
			} else {
				return configuration[activeSection].roadsListAttributeKeys;
			}
		}
	}
);

const getSelectedFeature = createSelector(
	[
		CommonSelect.selections.getActive,
		CommonSelect.maps.getMapLayerStateByMapKeyAndLayerKey, //TODO get feature data from here?
	],
	(activeSelection, layer) => {
		const selectedFeatureKey =
			activeSelection?.data?.featureKeysFilter?.keys?.[0];
		const selectionKey = activeSelection?.key;
		const features = layer?.options?.features;
		const fidColumnName = layer?.options?.fidColumnName;
		const hasLayerSelection = layer?.options?.selected?.[selectionKey];

		if (hasLayerSelection && selectedFeatureKey && features && fidColumnName) {
			return _find(
				features,
				f => f.properties[fidColumnName] === selectedFeatureKey
			);
		} else {
			return null;
		}
	}
);

const getSelectedPointFeature = createSelector(
	[
		CommonSelect.selections.getActive,
		interferometryDataSelectors.getAllByTypeByFeatureKey,
		state =>
			CommonSelect.app.getLocalConfiguration(
				state,
				'data.interferometry.tracks.types.activeKey'
			),
	],
	(activeSelection, featuresByType, activeType) => {
		const selectedFeatureKey =
			activeSelection?.data?.featureKeysFilter?.keys?.[0];
		if (selectedFeatureKey && !_isEmpty(featuresByType) && activeType) {
			return featuresByType[activeType]?.[selectedFeatureKey];
		} else {
			return null;
		}
	}
);

const getDataForPopup = createSelector(
	[
		getMapPopupAttributesOrder,
		getActiveVisualizationAttributes,
		(state, featureProperties) => featureProperties,
	],
	getFeatureDataWithAttributesOrdered
);

const getDataForAttributesList = createSelector(
	[
		getListAttributesOrder,
		getActiveVisualizationAttributes,
		(state, featureProperties) => featureProperties,
	],
	(
		attributesOrderGrouped,
		activeVisualizationAttributes,
		featureProperties
	) => {
		const data = [];
		if (featureProperties) {
			_forEach(attributesOrderGrouped, group => {
				const keys = group.keys;
				const attributes = [];
				_forEach(keys, key => {
					const value = featureProperties[key];
					const metadata = activeVisualizationAttributes[key] || {};

					if (key === 'cl_warn') {
						attributes.push({
							...metadata,
							value: enumerations[key]?.[value] || value,
							key,
						});
					} else if (key === 'chng_type') {
						attributes.push({
							...metadata,
							value: enumerations[key]?.[value] || '-',
							key,
						});
					} else {
						attributes.push({
							...metadata,
							value: value || value === 0 ? value : '-',
							key,
						});
					}

					if (key === 'ua_l3') {
						attributes.push({
							...metadata,
							name: 'Land use class name',
							value:
								styles.landUseClassification.attributeValues?.[value]?.name,
							key,
						});
					}
				});

				data.push({
					...group,
					attributes,
				});
			});
		}

		return data;
	}
);

// helpers
function getFeatureDataWithAttributesOrdered(
	attributesOrder,
	activeVisualizationAttributes,
	featureProperties
) {
	const data = [];
	_forEach(attributesOrder, key => {
		const value = featureProperties[key];
		const metadata = activeVisualizationAttributes[key] || {};

		data.push({
			...metadata,
			value: value || value === 0 ? value : '-',
			key,
		});
	});

	return data;
}

export default {
	...CommonSelect,
	specific: {
		interferometryData,
		eo4sdDrrInsarVietnam20: {
			getDataForPopup,
			getDataForAttributesList,
			getLayersStyleConfig,
			getListAttributesOrder,
			getMapPopupAttributesOrder,
			getPairConfig,
			getSelectedFeature,
			getSelectedPointFeature,
		},
	},
};
